/**
 * Cross-tree mirror of React Flow selection for chat composer and onboarding
 * steps outside the Liveblocks document subscription path.
 *
 * When a section is selected, direct children are included so chat surfaces
 * see the full section contents.
 */

import type { Node } from "@xyflow/react";
import { useNodes } from "@xyflow/react";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { expandSelectedNodesWithSectionChildren } from "../utils/canvasSelectionMirror";
import { useCanvasRoute } from "./CanvasRouteContext";

const EMPTY_SELECTION: Node[] = [];

type CanvasSelectionMirrorState = {
  canvasId: string | undefined;
  selectedNodes: Node[];
};

const DEFAULT_SELECTION_STATE: CanvasSelectionMirrorState = {
  canvasId: undefined,
  selectedNodes: EMPTY_SELECTION,
};

const CanvasSelectionMirrorContext = createContext<Node[]>(EMPTY_SELECTION);

type CanvasSelectionMirrorDispatchContextValue = {
  publish: (
    update: Partial<CanvasSelectionMirrorState> & { canvasId: string },
  ) => void;
  reset: () => void;
  getSelectedNodes: () => Node[];
};

const CanvasSelectionMirrorDispatchContext =
  createContext<CanvasSelectionMirrorDispatchContextValue | null>(null);

export function CanvasSelectionMirrorProvider({
  children,
}: {
  children: ReactNode;
}) {
  const [state, setState] = useState<CanvasSelectionMirrorState>(
    DEFAULT_SELECTION_STATE,
  );
  const selectedNodesRef = useRef<Node[]>(EMPTY_SELECTION);
  selectedNodesRef.current = state.selectedNodes;

  const publish = useCallback(
    (update: Partial<CanvasSelectionMirrorState> & { canvasId: string }) => {
      setState((prev) => ({
        canvasId: update.canvasId,
        selectedNodes: update.selectedNodes ?? prev.selectedNodes,
      }));
    },
    [],
  );

  const reset = useCallback(() => {
    setState(DEFAULT_SELECTION_STATE);
    selectedNodesRef.current = EMPTY_SELECTION;
  }, []);

  const getSelectedNodes = useCallback(() => selectedNodesRef.current, []);

  const dispatch = useMemo(
    () => ({ publish, reset, getSelectedNodes }),
    [publish, reset, getSelectedNodes],
  );

  return (
    <CanvasSelectionMirrorDispatchContext.Provider value={dispatch}>
      <CanvasSelectionMirrorContext.Provider value={state.selectedNodes}>
        {children}
      </CanvasSelectionMirrorContext.Provider>
    </CanvasSelectionMirrorDispatchContext.Provider>
  );
}

function useCanvasSelectionMirrorDispatch(): CanvasSelectionMirrorDispatchContextValue {
  const ctx = useContext(CanvasSelectionMirrorDispatchContext);
  if (!ctx) {
    throw new Error(
      "Canvas selection mirror hooks must be used within CanvasSelectionMirrorProvider",
    );
  }
  return ctx;
}

export function useCanvasSelectionMirror(): Node[] {
  return useContext(CanvasSelectionMirrorContext);
}

export function useCanvasSelectionMirrorGetter(): () => Node[] {
  const { getSelectedNodes } = useCanvasSelectionMirrorDispatch();
  return getSelectedNodes;
}

export function usePublishCanvasSelectionMirror(): void {
  const nodes = useNodes();
  const { canvasId } = useCanvasRoute();
  const { publish, reset } = useCanvasSelectionMirrorDispatch();

  const selectedNodes = useMemo(
    () => expandSelectedNodesWithSectionChildren(nodes),
    [nodes],
  );

  useEffect(() => {
    if (!canvasId) return;
    publish({ canvasId, selectedNodes });
  }, [canvasId, selectedNodes, publish]);

  useEffect(() => {
    return () => reset();
  }, [reset]);
}
